import { useQuery } from '@tanstack/react-query';
import { UserRound } from 'lucide-react';
import { useSession } from '@/components/auth/session';
import { LoadError } from '@/components/common/bits';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Surface } from '@/components/ui/section';
import { AgentsSection } from '@/features/account/AgentsSection';
import type { AccountData } from '@/features/account/data';
import { IdentityCard } from '@/features/account/IdentityCard';
import { PreferencesSection } from '@/features/account/PreferencesSection';
import { SecuritySection } from '@/features/account/SecuritySection';
import { UnmatchedNotice } from '@/features/account/UnmatchedNotice';
import { WorkSection } from '@/features/account/WorkSection';
import { RecentActivity } from '@/features/activity/RecentActivity';
import { feedItemsFromEvents } from '@/lib/activity';
import { api } from '@/lib/api';
import { hubKeys } from '@/lib/query';

const POLL = { refetchInterval: 30_000 } as const;

/**
 * Me: who the hub thinks you are, what your machines are doing, and the handful
 * of things you can actually change.
 *
 * The order reads from live state down to housekeeping, because the subject of
 * this page is the work and not the settings on it. What this replaces put the
 * password form second, a control a person touches roughly once a year, and put
 * the console preferences above the steps and jobs they came to read. Seven
 * regions of equal weight in an order nobody chose.
 *
 * 1. **Who you are**, once, in a card that is the identity rather than a header
 *    plus a panel repeating it.
 * 2. **Why the page is empty**, for somebody the hub could not match to
 *    anything, or matched to more than one person. It sits above the regions it
 *    explains, because read afterwards it explains nothing and four empty
 *    panels just look broken.
 * 3. **Your agents**, the only genuinely live state here: what is running right
 *    now, how much of the five-hour window is left, the concurrency, the repos
 *    each machine is mapped to, and pausing one. That last is the only device
 *    write the hub exposes and it is admin-gated on the server, so it is drawn
 *    for an admin and explained to everybody else.
 * 4. **Your work**, steps and jobs side by side rather than stacked as two
 *    panels of the same shape. Both have a page of their own, so this is the
 *    glance, not the list.
 * 5. **What already happened**, the activity strip. It stays because here it is
 *    scoped to you, which is a different list from the dashboard's.
 * 6. **Settings**, behind a hairline and an eyebrow: theme and density, then
 *    security, last. DESIGN.md spends space rather than borders between
 *    regions, and this is the one deliberate exception, because what changes at
 *    that line is the subject rather than the topic. Without it the eye reads
 *    seven regions of the same weight and has to work out which of them it came
 *    for.
 *
 * On a temporary password none of that is drawn: the hub answers 403 to every
 * read below, so the page is the identity card and the security card, in that
 * order, and the four queries are never asked.
 *
 * This file is the composition and nothing else. Every region lives in
 * `features/account`, because otherwise every change to any one of them lands
 * in the same file.
 *
 * `isViewer` is derived from empty data rather than from a role, because the
 * honest question is not "what are you allowed to see" but "is any of this
 * yours". Somebody with no device and no brief gets the org's numbers and an
 * explanation of why, instead of four empty panels that look broken.
 */
export function MePage() {
  const { session } = useSession();
  /**
   * On a temporary password the hub answers 403 to every read below, so none of
   * them is asked: the page is the identity card and the security card until
   * the password is the person's own.
   */
  const locked = session.mustChangePassword;
  const devices = useQuery({
    queryKey: hubKeys.myDevices,
    queryFn: api.myDevices,
    enabled: !locked,
    ...POLL,
  });
  const tasks = useQuery({
    queryKey: hubKeys.myTasks,
    queryFn: async () => (await api.myTasks()).tasks,
    enabled: !locked,
    ...POLL,
  });
  const briefs = useQuery({
    queryKey: hubKeys.myBriefs,
    queryFn: async () => (await api.myBriefs()).briefs,
    enabled: !locked,
    ...POLL,
  });
  const events = useQuery({
    queryKey: hubKeys.myEvents,
    queryFn: async () => (await api.myEvents()).events,
    enabled: !locked,
    ...POLL,
  });

  const data: AccountData = {
    devices: devices.data?.devices ?? [],
    account: devices.data?.account ?? null,
    tasks: tasks.data ?? [],
    briefs: briefs.data ?? [],
    loading: devices.isPending || tasks.isPending || briefs.isPending,
  };
  /**
   * A read that failed rather than came back empty. It has to be checked before
   * `isViewer` below, because the two are indistinguishable from `?? []`: a 500
   * on `/api/me/devices` drew the "nothing here is yours yet" onboarding at
   * somebody who owns half the fleet, and sent them looking for an invite.
   */
  const broken = [devices, tasks, briefs].find((query) => query.isError && !query.data);
  const isViewer =
    !data.loading && !broken && !data.devices.length && !data.briefs.length && !data.tasks.length;
  /**
   * The page is empty because the hub would not guess, not because this person
   * owns nothing. Worth its own region: the fix is a sign-in, and "nothing here
   * is yours yet" would send them looking for an invite instead.
   */
  const undecided = session.match === 'ambiguous';
  const unmatched = undecided || isViewer;

  return (
    <>
      <ViewHeader
        title="Your account"
        icon={UserRound}
        sub="identity, agents, your work, then settings"
        width="1180px"
      />
      <Page className="max-w-[1180px]">
        <div className="dy-stack">
          <IdentityCard data={data} />

          {!locked && broken ? (
            <Surface>
              <LoadError
                what="your work"
                error={broken.error}
                retry={() => void broken.refetch()}
              />
            </Surface>
          ) : null}

          {!locked && unmatched ? <UnmatchedNotice undecided={undecided} /> : null}

          {/* Everything the hub refuses while the password is temporary. */}
          {locked ? null : (
            <>
              <AgentsSection data={data} />
              <WorkSection data={data} />

              {/* The milestone strip, not the raw journal. `/api/me/events` is
                  already scoped to this person by the server, but scoping it does
                  not make it readable: their own slice is still mostly the
                  dispatcher offering, expiring and re-holding their tasks. See
                  `lib/activity.ts`. */}
              <RecentActivity
                items={feedItemsFromEvents(events.data ?? [])}
                scope="mine"
                limit={10}
              />

              {/* The one border on the page. Everything above it is the account;
                  everything below it is a setting on the account, which is a
                  change of subject and not just the next region. A hairline and
                  a caption on the page ground, taking the stack's own gap: no
                  surface, no card, nothing to mistake for a region of its own. */}
              <div className="border-line border-t pt-[var(--dy-gap)]">
                <div className="dy-eyebrow">Settings</div>
              </div>

              <PreferencesSection />
            </>
          )}

          {/* Last, and only for an account. The legacy token is a shared secret
              in the deployment's environment and this page is not where it
              changes. When the password is temporary this is the only other
              thing on the page, because the hub refuses everything above. */}
          {session.identity === 'session' ? <SecuritySection required={locked} /> : null}
        </div>
      </Page>
    </>
  );
}
